import { useState } from "react";
import { Moon, Sun, LogOut, User as UserIcon, Settings, Menu } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { MobileNav } from "@/components/Sidebar";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

function initialsFromName(name = "", email = "") {
  const src = (name || email || "").trim();
  if (!src) return "—";
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function Topbar() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const initials =
    user && typeof user === "object"
      ? initialsFromName(user.name, user.email)
      : "—";

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <>
      <header
        data-testid="topbar"
        className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-border bg-[hsl(var(--sipx-elevated))] px-3 sm:px-6"
      >
        <div className="flex min-w-0 items-center gap-2 sm:gap-3">
          <button
            type="button"
            data-testid="mobile-nav-trigger"
            onClick={() => setMobileOpen(true)}
            className="grid h-9 w-9 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-foreground md:hidden"
            aria-label="Menü megnyitása"
          >
            <Menu className="h-5 w-5" strokeWidth={1.75} />
          </button>
          <span className="hidden text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground sm:inline">
            SIPX Group
          </span>
          <span className="hidden text-muted-foreground/40 sm:inline">/</span>
          <span className="truncate text-[13px] font-medium tracking-tight sm:text-sm">
            Belső rendszer
          </span>
        </div>

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={toggleTheme}
            data-testid="theme-toggle"
            aria-label={theme === "dark" ? "Világos téma" : "Sötét téma"}
            className="grid h-9 w-9 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-foreground"
          >
            {theme === "dark" ? (
              <Sun className="h-4 w-4" strokeWidth={1.75} />
            ) : (
              <Moon className="h-4 w-4" strokeWidth={1.75} />
            )}
          </button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                data-testid="user-menu-trigger"
                className="ml-1 flex items-center gap-2.5 rounded-md py-1.5 pl-1.5 pr-2 transition-colors hover:bg-foreground/[0.06] sm:pr-2.5"
              >
                <span className="grid h-7 w-7 place-items-center rounded-full bg-foreground text-background text-[11px] font-semibold">
                  {initials}
                </span>
                <div className="hidden flex-col items-start leading-tight md:flex">
                  <span className="text-xs font-medium">
                    {user && typeof user === "object" ? user.name : ""}
                  </span>
                  <span className="text-[10px] text-muted-foreground">
                    {user && typeof user === "object" ? user.email : ""}
                  </span>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-56"
              data-testid="user-menu"
            >
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col">
                  <span className="text-sm font-medium">
                    {user && typeof user === "object" ? user.name : ""}
                  </span>
                  <span className="font-mono text-[11px] text-muted-foreground">
                    {user && typeof user === "object" ? user.email : ""}
                  </span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem disabled data-testid="menu-profile">
                <UserIcon className="h-4 w-4" strokeWidth={1.75} />
                Profil
              </DropdownMenuItem>
              <DropdownMenuItem disabled data-testid="menu-settings">
                <Settings className="h-4 w-4" strokeWidth={1.75} />
                Beállítások
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid="logout-button"
                onClick={handleLogout}
                className="text-foreground"
              >
                <LogOut className="h-4 w-4" strokeWidth={1.75} />
                Kilépés
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
      <MobileNav open={mobileOpen} onOpenChange={setMobileOpen} />
    </>
  );
}
